// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Divider from "@mui/material/Divider";
import Icon from "@mui/material/Icon";

// @mui icons

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

// Material Dashboard 2 React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DefaultProjectCard from "examples/Cards/ProjectCards/DefaultProjectCard";

// Overview page components
import Header from "./HeaderNoMargin";
import ProjectDetailsModal from "./ProjectDetailsModal";

// Data (not used on this page)
import React, { useCallback, useMemo } from "react";
import axios from "axios";
import PropTypes from "prop-types";

function Overview() {
  const [modalOpen, setModalOpen] = React.useState(false);
  const [selectedProject, setSelectedProject] = React.useState(null);
  const [profileData, setProfileData] = React.useState(null);
  const [activeProfileTab, setActiveProfileTab] = React.useState(0);
  const [profileTabData, setProfileTabData] = React.useState({
    feeDue: [],
    feeDueSummary: null,
    results: [],
    loading: false,
    error: "",
  });
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const user = useMemo(() => {
    try {
      const userStr = sessionStorage.getItem("user");
      return userStr ? JSON.parse(userStr) : null;
    } catch (err) {
      return null;
    }
  }, []);
  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;

  const getAuthHeaders = useCallback(() => {
    const token = sessionStorage.getItem("token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, []);

  // Fetch profile data from the API
  React.useEffect(() => {
    const fetchProfileData = async () => {
      try {
        setLoading(true);

        if (!user || !user.client_id) {
          setError("User information not found in session");
          setLoading(false);
          return;
        }

        // Build query params: include all available identifiers (email, student_id, user_id)
        // so the server can resolve with the best match available.
        const params = new URLSearchParams({
          client_id: user.client_id,
        });

        let addedAny = false;
        const emailFromUser = user.email || user.user_email;
        if (emailFromUser) {
          params.append("email", emailFromUser);
          addedAny = true;
        }

        if (user.student_id) {
          params.append("student_id", user.student_id);
          addedAny = true;
        }
        if (user.user_id) {
          params.append("user_id", user.user_id);
          addedAny = true;
        }
        if (!addedAny) {
          setError("No valid identifier found for user");
          setLoading(false);
          return;
        }

        if (!apiBaseUrl) {
          setError(
            "API base URL not configured. Please set REACT_APP_API_BASE_URL in your .env file."
          );
          setLoading(false);
          return;
        }

        const response = await axios.get(`${apiBaseUrl}/api/profile?${params.toString()}`, {
          headers: getAuthHeaders(),
        });

        if (response.data.success) {
          setProfileData(response.data.data);
        } else {
          setError(response.data.message || "Failed to fetch profile data");
        }
      } catch (err) {
        setError(err.response?.data?.message || err.message || "Failed to fetch profile data");
      } finally {
        setLoading(false);
      }
    };

    fetchProfileData();
  }, [apiBaseUrl, getAuthHeaders, user]);

  React.useEffect(() => {
    const student = profileData?.student;
    const collegeId =
      profileData?.college?.college_id || profileData?.student?.college_id || user?.college_id;

    if (!apiBaseUrl || !user?.client_id || !student?.student_id || !collegeId) {
      setProfileTabData((current) => ({
        ...current,
        feeDue: [],
        feeDueSummary: null,
        results: [],
        loading: false,
      }));
      return;
    }

    let cancelled = false;
    const fetchTabData = async () => {
      setProfileTabData((current) => ({ ...current, loading: true, error: "" }));
      try {
        const [feeResponse, resultResponse] = await Promise.all([
          axios.get(
            `${apiBaseUrl}/api/feereceipt-multiple/${user.client_id}/${collegeId}/student/${student.student_id}/fee-books`,
            { headers: getAuthHeaders() }
          ),
          axios.get(
            `${apiBaseUrl}/api/studentresult/student-final-results?student_id=${student.student_id}&client_id=${user.client_id}`,
            { headers: getAuthHeaders() }
          ),
        ]);

        if (!cancelled) {
          setProfileTabData({
            feeDue: feeResponse.data?.data || [],
            feeDueSummary: feeResponse.data?.summary || null,
            results: resultResponse.data?.data || [],
            loading: false,
            error: "",
          });
        }
      } catch (err) {
        if (!cancelled) {
          setProfileTabData({
            feeDue: [],
            feeDueSummary: null,
            results: [],
            loading: false,
            error: err.response?.data?.message || "Unable to load profile tab data",
          });
        }
      }
    };

    fetchTabData();

    return () => {
      cancelled = true;
    };
  }, [apiBaseUrl, getAuthHeaders, profileData, user]);

  const handleOpenModal = (project) => {
    setSelectedProject(project);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedProject(null);
  };

  // Loading state
  if (loading) {
    return (
      <DashboardLayout>
        <MDBox mb={2} />
        <MDBox display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
          <MDTypography variant="h6">Loading profile...</MDTypography>
        </MDBox>
      </DashboardLayout>
    );
  }

  // Error state
  if (error) {
    return (
      <DashboardLayout>
        <MDBox mb={2} />
        <MDBox display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
          <MDTypography variant="h6" color="error">
            Error: {error}
          </MDTypography>
        </MDBox>
      </DashboardLayout>
    );
  }

  // Extract data from profileData
  const student = profileData?.student || {};
  const studentExt = profileData?.studentExt || {};
  const branch = profileData?.branch || {};
  const batch = profileData?.batch || {};
  const college = profileData?.college || {};

  // Automatically fix photo_url if only roll number is present or the DB value is empty.
  const buildStudentPhotoUrls = () => {
    const rawPhoto = student.photo_url ? String(student.photo_url).trim() : "";
    const apiOrigin = apiBaseUrl ? apiBaseUrl.replace(/\/api\/?$/, "").replace(/\/$/, "") : "";

    const branchFolder = branch.branch_name ? branch.branch_name.replace(/\s+/g, "") : "";
    const batchFolder = batch.batch_name || batch.admittedbatch || "";
    const collegeId = college.college_id || student.college_id;
    const fallbackPath =
      student.student_rollno && collegeId && batchFolder && branchFolder
        ? `/uploads/photos/${collegeId}/${batchFolder}/${branchFolder}/${student.student_rollno}.png`
        : "";
    const fallbackUrl = fallbackPath ? `${apiOrigin}${encodeURI(fallbackPath)}` : "";

    if (!rawPhoto && student.student_rollno && collegeId && batchFolder && branchFolder) {
      return { primary: fallbackUrl, fallback: "" };
    }

    if (!rawPhoto) return { primary: "", fallback: fallbackUrl };
    if (rawPhoto.startsWith("data:")) return { primary: rawPhoto, fallback: fallbackUrl };

    if (/^https?:\/\//i.test(rawPhoto)) {
      try {
        const url = new URL(rawPhoto);
        if (apiOrigin && url.pathname.startsWith("/uploads")) {
          return {
            primary: `${apiOrigin}${encodeURI(url.pathname)}`,
            fallback: fallbackUrl,
          };
        }
      } catch (e) {
        // Use rawPhoto below if URL parsing fails.
      }
      return { primary: rawPhoto, fallback: fallbackUrl };
    }

    const fileName = rawPhoto.split(/[\\/]/).pop();
    const photoPath =
      !rawPhoto.includes("/") && collegeId && batchFolder && branchFolder
        ? `/uploads/photos/${collegeId}/${batchFolder}/${branchFolder}/${fileName}`
        : rawPhoto.startsWith("/")
        ? rawPhoto
        : `/${rawPhoto}`;

    return {
      primary: apiOrigin ? `${apiOrigin}${encodeURI(photoPath)}` : encodeURI(photoPath),
      fallback: fallbackUrl,
    };
  };

  const { primary: fixedPhotoUrl, fallback: fallbackPhotoUrl } = buildStudentPhotoUrls();

  // Fallback email for primary info: prefer student.email, else session user_email
  let fallbackUserEmail = student.email;
  if (!fallbackUserEmail) {
    try {
      const su = sessionStorage.getItem("user");
      fallbackUserEmail = su ? JSON.parse(su)?.user_email : undefined;
    } catch (e) {
      fallbackUserEmail = undefined;
    }
  }

  const formatDate = (value) => (value ? new Date(value).toLocaleDateString("en-GB") : "N/A");
  const valueOrNA = (value) => value || "N/A";

  const primaryInfo = {
    "Student ID": valueOrNA(student.student_id),
    "Roll No": valueOrNA(student.student_rollno),
    "Admission No": valueOrNA(student.admission_no),
    "Student Name": valueOrNA(student.student_name),
    Gender: valueOrNA(student.gender),
    "Date of Birth": formatDate(student.dob),
    Mobile: valueOrNA(student.mobile),
    Email: valueOrNA(student.email || fallbackUserEmail),
    Branch: valueOrNA(branch.branch_name || student.branch_name),
    Batch: valueOrNA(batch.batch_name || batch.admittedbatch),
    Status: valueOrNA(student.student_status),
  };

  const InfoPanel = ({ title, icon, items, accent }) => (
    <Card
      sx={{
        height: "100%",
        boxShadow: "0 6px 18px rgba(15, 23, 42, 0.08)",
        borderRadius: 2,
        border: "1px solid #e7edf5",
        overflow: "hidden",
      }}
    >
      <MDBox
        px={2}
        py={1.5}
        sx={{
          background: `linear-gradient(90deg, ${accent}14, #ffffff)`,
          borderBottom: "1px solid #e7edf5",
        }}
      >
        <MDBox display="flex" alignItems="center" gap={1.25}>
          <MDBox
            display="flex"
            alignItems="center"
            justifyContent="center"
            sx={{
              width: 34,
              height: 34,
              borderRadius: 1.25,
              backgroundColor: `${accent}18`,
              color: accent,
            }}
          >
            <Icon fontSize="small">{icon}</Icon>
          </MDBox>
          <MDTypography variant="h6" fontWeight="bold" color="dark">
            {title}
          </MDTypography>
        </MDBox>
      </MDBox>
      <MDBox p={2}>
        <MDBox display="grid" gap={1.1}>
          {Object.entries(items).map(([label, value]) => (
            <MDBox
              key={label}
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "140px minmax(0, 1fr)" },
                gap: { xs: 0.35, sm: 1.25 },
                alignItems: "start",
                px: 1.25,
                py: 0.85,
                borderRadius: 1,
                backgroundColor: "#f8fafc",
                border: "1px solid #edf2f7",
              }}
            >
              <MDTypography
                variant="button"
                fontWeight="bold"
                color="text"
                sx={{ fontSize: "0.82rem", lineHeight: 1.35 }}
              >
                {label}
              </MDTypography>
              <MDTypography
                variant="button"
                color="dark"
                sx={{
                  fontSize: "0.86rem",
                  lineHeight: 1.45,
                  fontWeight: 500,
                  wordBreak: "break-word",
                }}
              >
                {String(value)}
              </MDTypography>
            </MDBox>
          ))}
        </MDBox>
      </MDBox>
    </Card>
  );

  InfoPanel.propTypes = {
    title: PropTypes.string.isRequired,
    icon: PropTypes.string.isRequired,
    accent: PropTypes.string.isRequired,
    items: PropTypes.objectOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])).isRequired,
  };

  const money = (value) =>
    Number(value || 0).toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  const calculateResultSummary = () => {
    const results = profileTabData.results;
    const summary =
      results.find((row) => row.cgpa !== undefined && row.cgpa !== null) ||
      results.find((row) => row.percentage !== undefined && row.percentage !== null) ||
      results[0] ||
      {};
    const passed = results.filter(
      (row) => String(row.status || "").toUpperCase() === "PASS"
    ).length;
    const backlogCount = results.filter((row) => {
      const status = String(row.result || row.status || "").toUpperCase();
      return status && status !== "PASS";
    }).length;
    const totalCredits = results.reduce((sum, row) => {
      const credits = Number(row.credits ?? row.examCourse?.credits ?? 0);
      return Number.isFinite(credits) ? sum + credits : sum;
    }, 0);
    return {
      totalSubjects: results.length,
      passed,
      backlogCount,
      totalCredits: Math.round(totalCredits * 100) / 100,
      cgpa: summary.cgpa ?? summary.overall_cgpa ?? "N/A",
      percentage: summary.percentage ? `${Number(summary.percentage).toFixed(2)}%` : "N/A",
    };
  };

  const TabDataPanel = () => {
    const attendance = profileData?.attendanceSummary || {};
    const resultSummary = calculateResultSummary();
    const feeDueTotal =
      profileTabData.feeDueSummary?.total_due ??
      profileTabData.feeDue.reduce((sum, row) => sum + Number(row.due_amount || 0), 0);
    const panelConfig = [
      {
        title: "Attendance",
        icon: "event_available",
        color: "#1565c0",
        stats: {
          "Posted Days": attendance.posted_days || 0,
          Present: attendance.present_sessions || 0,
          "Working Sessions": attendance.working_sessions || 0,
          Percentage:
            attendance.present_percentage !== undefined && attendance.present_percentage !== null
              ? `${attendance.present_percentage}%`
              : "N/A",
        },
        rows: attendance.semester_records || [],
        emptyText: "No attendance records found.",
      },
      {
        title: "Fee Due",
        icon: "account_balance_wallet",
        color: "#c62828",
        stats: {
          "Total Due": `Rs. ${money(feeDueTotal)}`,
        },
        rows: [],
        emptyText: "",
      },
      {
        title: "Result",
        icon: "workspace_premium",
        color: "#2e7d32",
        stats: {
          Subjects: resultSummary.totalSubjects,
          Passed: resultSummary.passed,
          Backlogs: resultSummary.backlogCount,
          "Total Credits": resultSummary.totalCredits,
          CGPA: resultSummary.cgpa,
          Percentage: resultSummary.percentage,
        },
        rows: [],
        emptyText: "No result records found.",
      },
    ];
    const selected = panelConfig[activeProfileTab] || panelConfig[0];

    const renderRow = (row, index) => {
      if (activeProfileTab === 0) {
        const percentage =
          row.present_percentage !== undefined && row.present_percentage !== null
            ? `${Number(row.present_percentage).toFixed(2)}%`
            : "N/A";
        const presentDays =
          row.present_days !== undefined && row.present_days !== null
            ? Number(row.present_days).toFixed(1)
            : "0.0";
        return `Year ${row.year_number || "N/A"} - Semester ${
          row.semester_number || index + 1
        }: ${percentage} (${presentDays}/${row.posted_days || 0})`;
      }
      if (activeProfileTab === 1) {
        const feeHead = row.feeHead?.fee_head_name || row.fee_head_name || "Fee";
        return `${feeHead} - Due: Rs. ${money(row.due_amount || 0)}`;
      }
      const course =
        row.course_name || row.subject_name || row.course_code || `Subject ${index + 1}`;
      const grade = row.grade || row.status || "N/A";
      return `${course} - Grade: ${grade}`;
    };

    return (
      <MDBox
        p={2.25}
        sx={{
          height: "100%",
          backgroundColor: "white",
          borderRadius: 2,
          boxShadow: "0 6px 18px rgba(15, 23, 42, 0.08)",
          border: "1px solid #e7edf5",
        }}
      >
        <MDBox display="flex" alignItems="center" gap={1} mb={1.5}>
          <Icon sx={{ color: selected.color }}>{selected.icon}</Icon>
          <MDTypography variant="h6" fontWeight="bold" color="dark">
            {selected.title}
          </MDTypography>
        </MDBox>
        {profileTabData.loading ? (
          <MDTypography variant="button" color="text">
            Loading data...
          </MDTypography>
        ) : profileTabData.error ? (
          <MDTypography variant="button" color="error">
            {profileTabData.error}
          </MDTypography>
        ) : (
          <>
            <MDBox display="grid" gridTemplateColumns="repeat(2, minmax(0, 1fr))" gap={1} mb={1.5}>
              {Object.entries(selected.stats).map(([label, value]) => (
                <MDBox
                  key={label}
                  sx={{
                    p: 1,
                    borderRadius: 1,
                    border: "1px solid #edf2f7",
                    backgroundColor: "#f8fafc",
                  }}
                >
                  <MDTypography variant="caption" color="text" fontWeight="bold" display="block">
                    {label}
                  </MDTypography>
                  <MDTypography variant="button" color="dark" fontWeight="bold">
                    {String(value)}
                  </MDTypography>
                </MDBox>
              ))}
            </MDBox>
            {activeProfileTab === 1 || activeProfileTab === 2 ? null : selected.rows.length ? (
              selected.rows.map((row, index) => (
                <MDTypography
                  key={`${selected.title}-${index}`}
                  variant="button"
                  color="dark"
                  display="block"
                  mb={1}
                  sx={{ fontSize: "0.88rem", lineHeight: 1.45 }}
                >
                  - {renderRow(row, index)}
                </MDTypography>
              ))
            ) : (
              <MDTypography
                variant="button"
                color="text"
                display="block"
                sx={{ fontSize: "0.88rem" }}
              >
                {selected.emptyText}
              </MDTypography>
            )}
          </>
        )}
      </MDBox>
    );
  };

  return (
    <>
      <Header
        tabValue={activeProfileTab}
        onTabChange={setActiveProfileTab}
        profileData={{
          ...profileData,
          student: {
            ...student,
            photo_url: fixedPhotoUrl,
            photo_fallback_url: fallbackPhotoUrl,
          },
        }}
      >
        <MDBox mt={3} mb={3} px={0}>
          <Grid container spacing={2.25} alignItems="stretch">
            <Grid item xs={12} md={4} xl={4} sx={{ order: { xs: 2, md: 1 } }}>
              <InfoPanel
                title="Primary Information"
                icon="badge"
                items={primaryInfo}
                accent="#1a73e8"
              />
            </Grid>
            <Grid item xs={12} md={4} xl={4} sx={{ display: "flex", order: { xs: 3, md: 2 } }}>
              <InfoPanel
                title="Secondary Information"
                icon="family_restroom"
                accent="#00897b"
                items={{
                  "Father Name": student.father_name || "N/A",
                  "Mother Name": student.mother_name || "N/A",
                  "Father Occupation": studentExt.fatherocc || "N/A",
                  "Mother Occupation": studentExt.motherocc || "N/A",
                  "Family Income":
                    typeof studentExt.income === "number"
                      ? studentExt.income.toString()
                      : studentExt.income || "N/A",
                  "Father Phone No": studentExt.fatherphno || "N/A",
                  "Mother Phone No": studentExt.motherphno || "N/A",
                  "Blood Group": studentExt.bgroup || "N/A",
                  "PHC Type": studentExt.phcd || "N/A",
                }}
              />
            </Grid>
            <Grid item xs={12} md={4} xl={4} sx={{ order: { xs: 1, md: 3 } }}>
              <TabDataPanel />
            </Grid>
          </Grid>
        </MDBox>
        {/* Additional Information Section */}
        <MDBox pt={2} px={0} lineHeight={1.25}>
          <MDTypography variant="h6" fontWeight="medium">
            ADDITIONAL INFORMATION
          </MDTypography>
          <MDBox mb={1}>
            <MDTypography variant="button" color="text">
              Extended student information and academic details
            </MDTypography>
          </MDBox>
        </MDBox>
        <MDBox p={0} mt={2}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6} xl={3}>
              <DefaultProjectCard
                label=""
                title="👤 PERSONAL"
                description={`Father: ${student.father_name || "N/A"}, Mother: ${
                  student.mother_name || "N/A"
                }`}
                action={{
                  type: "internal",
                  route: "#",
                  color: "info",
                  label: "view details",
                }}
                onClick={() =>
                  handleOpenModal({
                    title: "PERSONAL INFORMATION",
                    details: {
                      "Father Name": student.father_name || "N/A",
                      "Mother Name": student.mother_name || "N/A",
                      "Date of Birth": student.dob
                        ? new Date(student.dob).toLocaleDateString()
                        : "N/A",
                      Gender: student.gender || "N/A",
                      "Aadhaar No": student.aadhaar_no || "N/A",
                      Mobile: student.mobile || "N/A",
                      Email: student.email || "N/A",
                      Address: student.address || "N/A",
                      "Blood Group": studentExt.bgroup || "N/A",
                      "Mother Tongue": studentExt.mtongue || "N/A",
                      Nationality: studentExt.nationality || "N/A",
                      Religion: studentExt.religion || "N/A",
                    },
                  })
                }
              />
            </Grid>
            <Grid item xs={12} md={6} xl={3}>
              <DefaultProjectCard
                label=""
                title="📝 ADMISSION"
                description={`Admission No: ${student.admission_no || "N/A"}, Date: ${
                  student.admission_date || "N/A"
                }`}
                action={{
                  type: "internal",
                  route: "#",
                  color: "info",
                  label: "view details",
                }}
                onClick={() =>
                  handleOpenModal({
                    title: "ADMISSION DETAILS",
                    details: {
                      "Admission No": student.admission_no || "N/A",
                      "Admission Date": student.admission_date || "N/A",
                      "Roll No": student.student_rollno || "N/A",
                      Batch: batch.batch_name || "N/A",
                      Branch: branch.branch_name || "N/A",
                      College: college.college_name || "N/A",
                      "Admission Type": student.admission_type || "N/A",
                      "Seat Type": student.seat_type || "N/A",
                      "Entrance Type": studentExt.entrancetype || "N/A",
                      "Entrance Rank": studentExt.entrance_rank?.toString() || "N/A",
                      "Student Status": student.student_status || "N/A",
                    },
                  })
                }
              />
            </Grid>
            <Grid item xs={12} md={6} xl={3}>
              <DefaultProjectCard
                label=""
                title="🎓 ACADEMICS"
                description={`SSC: ${
                  studentExt.sscpercent ? studentExt.sscpercent + "%" : "N/A"
                }, Inter: ${studentExt.idpercent ? studentExt.idpercent + "%" : "N/A"}`}
                action={{
                  type: "internal",
                  route: "#",
                  color: "info",
                  label: "view details",
                }}
                onClick={() =>
                  handleOpenModal({
                    title: "ACADEMIC DETAILS",
                    details: {
                      "SSC School": studentExt.sscsname || "N/A",
                      "SSC Percentage": studentExt.sscpercent ? studentExt.sscpercent + "%" : "N/A",
                      "SSC Grade": studentExt.sscgrade || "N/A",
                      "SSC Year": studentExt.sscyear || "N/A",
                      "SSC Board": studentExt.sscboard || "N/A",
                      "Inter College": studentExt.idcollegename || "N/A",
                      "Inter Percentage": studentExt.idpercent ? studentExt.idpercent + "%" : "N/A",
                      "Inter Grade": studentExt.idgrade || "N/A",
                      "Inter Year": studentExt.idyear || "N/A",
                      "Inter Board": studentExt.idboard || "N/A",
                      "Degree College": studentExt.dgcollegename || "N/A",
                      "Degree Percentage": studentExt.dgpercent
                        ? studentExt.dgpercent + "%"
                        : "N/A",
                    },
                  })
                }
              />
            </Grid>
            <Grid item xs={12} md={6} xl={3}>
              <DefaultProjectCard
                label=""
                title="📍 ADDRESS & CONTACT"
                description={student.address || "Address information"}
                action={{
                  type: "internal",
                  route: "#",
                  color: "info",
                  label: "view details",
                }}
                onClick={() =>
                  handleOpenModal({
                    title: "ADDRESS & CONTACT DETAILS",
                    details: {
                      Address: student.address || "N/A",
                      Mobile: student.mobile || "N/A",
                      Email: student.email || "N/A",
                      "Father Phone": studentExt.fatherphno || "N/A",
                      "Mother Phone": studentExt.motherphno || "N/A",
                      "Father Occupation": studentExt.fatherocc || "N/A",
                      "Mother Occupation": studentExt.motherocc || "N/A",
                      "Bank Name": studentExt.bankname || "N/A",
                      "Bank Account": studentExt.bankno || "N/A",
                      "IFSC Code": studentExt.ifsc || "N/A",
                    },
                  })
                }
              />
            </Grid>

            {/* Removed separate Secondary Information card since it's now the main info card */}
          </Grid>
        </MDBox>
      </Header>
      <ProjectDetailsModal open={modalOpen} onClose={handleCloseModal} project={selectedProject} />
    </>
  );
}
export default Overview;
